import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { focusElement } from 'platform/utilities/ui';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import api from '../utilities/api';
import store from '../utilities/store';
import ClaimantNotFound from '../components/ClaimantNotFound';
import QuickSubmitEmbed from '../components/QuickSubmitEmbed';
import QuickSubmitLeaveGuard from '../components/QuickSubmitLeaveGuard';
import {
  EFORM_VIEW,
  QUICK_SUBMIT_BC_LABEL,
  QUICK_SUBMIT_BASE,
  QUICK_SUBMIT_EMBED_PAGES,
  QUICK_SUBMIT_LABELS,
  QUICK_SUBMIT_PAGES,
  eformFromRoute,
  eformHeading,
  eformState,
  fetchEmbedClaimant,
  loadQuickSubmitClaimant,
  quickSubmitBC,
  quickSubmitTitle,
  quickSubmitUrls,
  shouldRevalidateQuickSubmit,
  useQuickSubmitPage,
  useQuickSubmitRouteLoaderData,
  withBasename,
  withoutBasename,
} from '../utilities/quickSubmit';
import { SUBMISSIONS_PATH, SUBMIT_FORMS_PATH } from '../utilities/constants';

// The parent route draws this once for every QuickSubmit page. A move between
// them changes only the child route, so the embed below stays in the DOM and
// keeps its state.
const QuickSubmitLayout = () => {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const isSubmissionsUpdateEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateMain,
  );

  const page = useQuickSubmitPage();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const {
    claimant = null,
    claimantId = null,
    claimantUnavailable = false,
    notRepresented = false,
    overview = null,
    quickSubmitDisabled = false,
  } = useQuickSubmitRouteLoaderData();
  const urls = quickSubmitUrls({ claimantId });

  // The embed runs this while it mints its session, so neither waits on the
  // other.
  const loadEmbedClaimant = useCallback(
    signal => fetchEmbedClaimant(api, { claimantId, overview, signal }),
    [claimantId, overview],
  );

  // The eForm open on the eForms page, if any. Its ids are in the URL.
  const eform =
    page === QUICK_SUBMIT_PAGES.EFORMS
      ? eformFromRoute(params, location.state)
      : null;
  const heading = eform ? eformHeading(eform) : QUICK_SUBMIT_LABELS[page];

  // The last eForm shown. "Continue editing" asks for the form back with its
  // ids only, after the URL has already left it, so its name comes from here.
  const lastEform = useRef(null);
  if (eform) lastEform.current = eform;

  // True while leaving would lose work. The embed reports it as a plain
  // boolean through callbacks.onDirtyChange. The outlet context carries the
  // same handler, so a page can report work of its own.
  const [dirty, setDirty] = useState(false);
  const onDirtyChange = useCallback(value => setDirty(value === true), []);
  const outletContext = useMemo(() => ({ onDirtyChange }), [onDirtyChange]);

  const headingRef = useRef(null);
  // One key per screen: the page, plus the open eForm.
  const screen = `${page}|${eform?.formId ?? ''}|${eform?.submissionId ?? ''}`;
  const lastScreen = useRef(screen);

  useEffect(() => {
    document.title = quickSubmitTitle(heading);
  }, [heading]);

  // A route change is not a page load, so a screen reader hears nothing and
  // focus stays on the link that moved it. Move it to the new screen's h1. The first load
  // keeps the browser's own start point.
  useEffect(() => {
    if (lastScreen.current === screen) return;
    lastScreen.current = screen;
    focusElement(headingRef.current);
  }, [screen]);

  // A router-link breadcrumb. The trail marks only in-app links that way.
  const handleRouteChange = useCallback(
    ({ detail }) => {
      const path = withoutBasename(detail?.href);
      if (path) navigate(path);
    },
    [navigate],
  );

  // The embed asked to change section. The URL moves here, and the layout
  // then rewrites the view on the same element. A host that passes
  // onNavigate owns every move, so each destination is answered.
  const handleNavigate = ({
    destination,
    formId,
    submissionId,
    formNumber,
    title,
    replace,
  }) => {
    const options = { replace: !!replace };
    if (destination === QUICK_SUBMIT_PAGES.UPLOAD) {
      navigate(urls.upload, options);
    } else if (destination === QUICK_SUBMIT_PAGES.EFORMS) {
      navigate(urls.eforms, options);
    } else if (destination === EFORM_VIEW && formId != null) {
      // replace comes from the embed's "Continue editing": the rep declined
      // to leave an unsaved eForm, so the URL goes back. The request has no
      // name then, so keep the name of the form it names.
      const last =
        lastEform.current?.formId === `${formId}` ? lastEform.current : {};
      navigate(urls.eform({ formId, submissionId }), {
        ...options,
        state: eformState({
          formId,
          formNumber: formNumber || last.formNumber,
          title: title || last.title,
        }),
      });
    }
  };

  // The packet is sent. Replace, so Back does not offer a second submit.
  // Leaving removes the element, which is fine now. The embed is still dirty
  // here (it clears the sent files after onComplete), and nothing is left to
  // lose, so stop guarding. Without this, a link out of the confirmation asks.
  // History state keeps the file names out of the URL and survives a reload.
  const handleComplete = ({ correlationId, packetStatus, files, veteran }) => {
    setDirty(false);
    navigate(urls.confirmation, {
      replace: true,
      state: {
        correlationId,
        packetStatus,
        files,
        veteran,
        submittedAt: new Date().toISOString(),
      },
    });
  };

  if (notRepresented) return <ClaimantNotFound />;

  // The flag is off. Match the sibling pages that are not live yet.
  if (quickSubmitDisabled) {
    return (
      <section className="vads-u-width--full">
        <h1 ref={headingRef} data-testid="quick-submit-heading">
          {QUICK_SUBMIT_LABELS[page]}
        </h1>
        <p data-testid="qs-unavailable">This page isn’t available yet.</p>
        <va-link-action
          href={
            isSubmissionsUpdateEnabled ? SUBMIT_FORMS_PATH : SUBMISSIONS_PATH
          }
          text="Go to Submissions page"
          type="primary"
        />
      </section>
    );
  }

  const showsEmbed = QUICK_SUBMIT_EMBED_PAGES.includes(page);

  return (
    <section className="vads-u-width--full">
      {/* This wrapper stops a long trail from clipping. */}
      <div className="claimant-details-submission-history__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={quickSubmitBC({ claimantId, page, eform })}
          label={QUICK_SUBMIT_BC_LABEL}
          homeVeteransAffairs={false}
          onRouteChange={handleRouteChange}
          wrapping
        />
      </div>

      {/* A route change, not a page load, so the embed and the files
          attached on upload stay. */}
      {page === QUICK_SUBMIT_PAGES.EFORMS && !eform && (
        <p>
          <va-link
            back
            href={withBasename(urls.upload)}
            text="Back to upload"
            data-testid="qs-back-to-upload"
            onClick={event => {
              event.preventDefault();
              navigate(urls.upload);
            }}
          />
        </p>
      )}
      {/* The prototype spaces the h1 per page: upload keeps its intro close. */}
      <h1
        ref={headingRef}
        data-testid="quick-submit-heading"
        className={`vads-u-margin-top--4 ${
          page === QUICK_SUBMIT_PAGES.EFORMS
            ? 'vads-u-margin-bottom--3'
            : 'vads-u-margin-bottom--1'
        }`}
      >
        {heading}
      </h1>
      {page === QUICK_SUBMIT_PAGES.UPLOAD && (
        <p className="vads-u-measure--5" data-testid="qs-intro">
          Submit completed VA forms and supporting documents for a Veteran you
          represent. Attach files you already have, or fill out an eForm online
          and add it to this upload.
        </p>
      )}
      {/* The confirmation names the claimant in its alert. */}
      {claimant && page !== QUICK_SUBMIT_PAGES.CONFIRMATION && (
        <p
          className="vads-u-margin-top--0 vads-u-margin-bottom--2"
          data-testid="qs-claimant-name"
        >
          Submitting for{' '}
          <strong>
            {claimant.lastName}, {claimant.firstName}
          </strong>
        </p>
      )}
      {claimantUnavailable && (
        <va-alert
          status="warning"
          visible
          class="vads-u-margin-bottom--3"
          data-testid="qs-claimant-unavailable"
        >
          <h2 slot="headline">We can’t load this claimant’s information</h2>
          <p className="vads-u-margin-bottom--0">
            You can still enter their information yourself.
          </p>
        </va-alert>
      )}
      <Outlet context={outletContext} />
      {/* One element for upload and eForms. A move between them or an open
          eForm only changes its view, and the embed moves in place. A new
          claimant gets a new element on a new session, so one claimant's
          work never carries over to the next. Leaving for the confirmation removes it,
          which is fine: the packet is sent. */}
      {showsEmbed && (
        <QuickSubmitEmbed
          key={claimantId ?? ''}
          view={eform ? EFORM_VIEW : page}
          formId={eform?.formId}
          submissionId={eform?.submissionId}
          claimantId={claimantId}
          loadClaimant={loadEmbedClaimant}
          onComplete={handleComplete}
          onNavigate={handleNavigate}
          onDirtyChange={onDirtyChange}
        />
      )}
      {/* handleComplete clears dirty before it moves to the confirmation, so
          a link out of the confirmation never asks. */}
      <QuickSubmitLeaveGuard
        hasUnsavedWork={dirty}
        sectionPathname={QUICK_SUBMIT_BASE}
      />
    </section>
  );
};

// Loaded once on the parent route, so a move between pages does not fetch
// again.
QuickSubmitLayout.loader = loadQuickSubmitClaimant(api, store);
QuickSubmitLayout.shouldRevalidate = shouldRevalidateQuickSubmit;

export default QuickSubmitLayout;
